import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {DropDownItem} from "../../../../models/general/dropdownItem.model";
import {PostFile} from "../../../../models/general/system-models/post-file";
import { RefDataService } from 'app/services/general/refData.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BaseResultModel } from 'app/models';
import { PostCatToolAnalysis } from '../models/cat-tool-analysis';
import { FormControl } from '@angular/forms';
import { NotificationService } from 'app/services/notification.service';
import {FileAutoUpload} from "../../../../components/drag-and-drop-file/drag-and-drop-file.component";

// System-reserved CatTool row representing the Trados XML schema. Must match the seeded
// `CatTool.Id = 1` row on the backend; the file-type cross-check below depends on this id.
const XML_CAT_TOOL_ID = 1;

// The tenant's "Robert" CatTool row (seeded on every tenant; ids differ per tenant, so it is found BY NAME).
// With the Robert integration on, picking it pulls the analysis from Robert instead of uploading a file.
const ROBERT_CAT_TOOL_NAME = 'robert';

@Component({
  selector: 'tp-cat-count-analyse',
  templateUrl: './cat-count-analyse.component.html',
  styleUrls: ['./cat-count-analyse.component.scss']
})
export class CatCountAnalyseComponent implements OnInit, OnDestroy {

    @Output() outFiles=new EventEmitter<PostFile[]>();
    @Output() outCatAnalysis=new EventEmitter<PostCatToolAnalysis>();
    @Output() openAi=new EventEmitter<void>();
    // Robert (CAT tool) integration: when the host says the job is in Robert, the "Robert" analysis type
    // switches this form to PULL mode (no upload) and emits the Robert CatTool id (null = Robert deselected).
    @Input() robertAvailable = false;
    // Pre-select "Robert" once the options arrive (the host saw the Robert analysis is ready).
    @Input() preselectRobert = false;
    @Output() outRobert = new EventEmitter<number | null>();

    private obs$:Subject<void>=new Subject<void>();

    analysisType:FormControl=new FormControl();
    
    catTools: DropDownItem[] = [];
    files: PostFile[] = [];
    autoUpload: Partial<FileAutoUpload> = { status: false };

    constructor(
      private _refDataService:RefDataService,
      private _notificationService:NotificationService
    ) 
    { }

    ngOnInit(): void {
      this._refDataService.getCatTools()
      .pipe(takeUntil(this.obs$))
      .subscribe(
        (res:BaseResultModel<DropDownItem[]>)=>{
          if(res.status){
            this.catTools=res.response;
            const robertId = this.robertCatToolId;
            if (this.robertAvailable && this.preselectRobert && robertId != null) {
              this.analysisType.setValue(robertId);
              this.getSelectedCatTool(robertId);
            }
          }
        }
      );
    }

    // The tenant's "Robert" CatTool option id (by name, case-insensitive), or null when the tenant has none.
    get robertCatToolId(): number | null {
      const hit = (this.catTools ?? []).find(t => String(t.name ?? '').trim().toLowerCase() === ROBERT_CAT_TOOL_NAME);
      return hit ? Number(hit.id) : null;
    }

    // True when the picked analysis type is "Robert" AND the job is in Robert → pull mode (upload hidden).
    get isRobertSelected(): boolean {
      const id = this.robertCatToolId;
      return this.robertAvailable && id != null && Number(this.analysisType.value) === id;
    }

    ngOnDestroy():void{
      this.obs$.next();
      this.obs$.complete();
    }

    saveFiles(){
      this.outFiles.emit(this.files);
    }

    getSelectedFiles(e:PostFile[]){
      this.files=[];

      if(e){
        this.files=[...this.files,e[0]];

        const file:File=this.files[0].file;

        //check file type
        if(!this.allowedFormats(file.name)){
          this._notificationService.showError('Only .csv, .xlsx, and .xml files are allowed to be uploaded','Warning');
          this.files=[];
          return;
        }

        if(this.analysisType.value){
          if(!this.fileMatchesSchema(file.name, Number(this.analysisType.value))){
            this.files=[];
            return;
          }

          const postCatAnalysis:PostCatToolAnalysis=<PostCatToolAnalysis>{
            csvFile: this.files[0].file,
            catToolId: Number(this.analysisType.value),
            clientId:0,
            resourceId:0
          };

          this.outCatAnalysis.emit(postCatAnalysis);
          this.outFiles.emit(this.files);
        }
      }

    }

    getSelectedCatTool(e:number){
      if(e){

        this.analysisType.setValue(e);
        // Robert pull mode: no file is involved — tell the host and skip the upload/schema checks.
        if (this.isRobertSelected) {
          this.files = [];
          this.outFiles.emit(this.files);
          this.outRobert.emit(Number(e));
          return;
        }
        this.outRobert.emit(null);
        //check if user has uploaded a file
        if(this.files?.length>0){
          const fileName=this.files[0]?.file.name;
          //check file type
          if(!this.allowedFormats(fileName)){
            this._notificationService.showError('Only .csv, .xlsx, and .xml files are allowed to be uploaded','Warning');
            return;
          }

          if(!this.fileMatchesSchema(fileName, Number(e))){
            this.files=[];
            return;
          }

          const postCatAnalysis:PostCatToolAnalysis=<PostCatToolAnalysis>{
            csvFile: this.files[0].file,
            catToolId: Number(e),
            clientId:0,
            resourceId:0
          };

          this.outCatAnalysis.emit(postCatAnalysis);
          this.outFiles.emit(this.files);
        }
      }
    }

    /**
     * Cross-validate the uploaded file extension against the chosen schema. The reserved XML
     * schema (id = 1) accepts only .xml; every other (column-based) schema accepts only
     * .csv/.xlsx. Surfaces the same wording as the backend validator for consistency.
     */
    private fileMatchesSchema(filename: string, catToolId: number): boolean {
      const ext = this.getExtension(filename)?.toLowerCase();
      const isXmlFile = ext === 'xml';
      const isXmlSchema = catToolId === XML_CAT_TOOL_ID;

      if (isXmlSchema && !isXmlFile) {
        this._notificationService.showError(
          "The 'XML (Trados)' schema only accepts .xml files.", 'Warning');
        return false;
      }
      if (!isXmlSchema && isXmlFile) {
        this._notificationService.showError(
          "XML files require the 'XML (Trados)' schema.", 'Warning');
        return false;
      }
      return true;
    }

    getExtension(filename) {
      var parts = filename.split('.');
      return parts[parts.length - 1];
    }
    
    allowedFormats(filename) {
      var ext = this.getExtension(filename);
      switch (ext.toLowerCase()) {
        case 'csv':
          return true;
        case 'xlsx':
            return true;
        case 'xml':
            return true;
      }
      return false;
    }

}